import React, { useState, useEffect } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';

const STATES_URL = 'https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome';
const CITIES_URL = (uf) =>
    `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios?orderBy=nome`;

export default function CityStateSelect({ value, onChange, name, disabled, sx }) {
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [selectedState, setSelectedState] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);

    // Load states on mount
    useEffect(() => {
        setLoadingStates(true);
        fetch(STATES_URL)
            .then((res) => res.json())
            .then((data) => {
                setStates(data.map((s) => ({ id: s.id, sigla: s.sigla, nome: s.nome })));
            })
            .catch(() => setStates([]))
            .finally(() => setLoadingStates(false));
    }, []);

    // Parse initial value (e.g. "Brasília-DF") when states are loaded
    useEffect(() => {
        if (!value || !states.length || selectedState) return;

        const lastDash = value.lastIndexOf('-');
        if (lastDash === -1) return;

        const cityName = value.substring(0, lastDash).trim();
        const uf = value.substring(lastDash + 1).trim().toUpperCase();

        const state = states.find((s) => s.sigla === uf);
        if (state) {
            setSelectedState(state);
            // Cities will be loaded by the next useEffect; we store the city name to select it after loading
            setSelectedCity({ nome: cityName });
        }
    }, [value, states]);

    // Load cities when state changes
    useEffect(() => {
        if (!selectedState) {
            setCities([]);
            return;
        }

        setLoadingCities(true);
        fetch(CITIES_URL(selectedState.sigla))
            .then((res) => res.json())
            .then((data) => {
                const cityList = data.map((c) => ({ id: c.id, nome: c.nome }));
                setCities(cityList);

                // If we have a pending city name from parse, match it
                if (selectedCity?.nome) {
                    const match = cityList.find((c) => c.nome === selectedCity.nome);
                    if (match) {
                        setSelectedCity(match);
                    } else {
                        setSelectedCity(null);
                    }
                }
            })
            .catch(() => setCities([]))
            .finally(() => setLoadingCities(false));
    }, [selectedState?.sigla]);

    const handleStateChange = (_event, newState) => {
        setSelectedState(newState);
        setSelectedCity(null);
        if (!newState) {
            onChange(name, '');
        }
    };

    const handleCityChange = (_event, newCity) => {
        setSelectedCity(newCity);
        if (newCity && selectedState) {
            onChange(name, `${newCity.nome}-${selectedState.sigla}`);
        } else {
            onChange(name, '');
        }
    };

    return (
        <Grid container spacing={2} sx={{ ml: 0, width: '100%' }}>
            <Grid item xs={5} sx={{ pl: '0px !important' }}>
                <Autocomplete
                    options={states}
                    getOptionLabel={(option) => option.sigla || ''}
                    isOptionEqualToValue={(option, val) => option.sigla === val?.sigla}
                    value={selectedState}
                    onChange={handleStateChange}
                    loading={loadingStates}
                    disabled={disabled}
                    size="small"
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Estado"
                            variant="outlined"
                            required
                            size="small"
                            sx={sx}
                        />
                    )}
                />
            </Grid>
            <Grid item xs={7}>
                <Autocomplete
                    options={cities}
                    getOptionLabel={(option) => option.nome || ''}
                    isOptionEqualToValue={(option, val) => option.id === val?.id}
                    value={selectedCity?.id ? selectedCity : null}
                    onChange={handleCityChange}
                    loading={loadingCities}
                    disabled={disabled || !selectedState}
                    noOptionsText={selectedState ? 'Nenhuma cidade encontrada' : 'Selecione um estado'}
                    size="small"
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Cidade"
                            variant="outlined"
                            required
                            size="small"
                            sx={sx}
                        />
                    )}
                />
            </Grid>
        </Grid>
    );
}
